import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Collapse,
} from "@material-tailwind/react";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Label from "components/Label/Label";

interface ServiceDetailsPageProps {
  open: boolean;
  handleClose?: () => void;
  data: {
    id?: number;
    title?: string;
    description?: string;
    images?: string[];
    events?: string[];

    region?: string;
    enable?: boolean;
    validation?: boolean;
    createdAt?: string;
    updatedAt?: string;
    serviceAgentId?: number;
    ServiceAgent?: {
      designation?: string;
      availability?: string[];
      contact?: string;
      phone?: string;
      link?: string;
      facebook?: string;
      instagram?: string;
      youtube?: string;
    };
  } | null;
}

const ServiceDetailsPage: React.FC<ServiceDetailsPageProps> = ({ open, handleClose, data }) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [openCollapse, setOpenCollapse] = useState({
    events: false,
  });

  // Toggle collapse sections
  const toggleCollapse = (section: keyof typeof openCollapse) => {
    setOpenCollapse((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Function to clean and format description
  const formatDescription = (description?: string) => {
    if (!description) return "N/A";
    return description.replace(/\r\n/g, " ").replace(/\s+/g, " ").trim();
  };

  // Field icons using Line Awesome
  const fieldIcons = {
    title: "las la-tag",
    description: "las la-file-alt",
    serviceAgent: "las la-user-tie",
    availability: "las la-calendar-check",
    events: "las la-glass-cheers",

    region: "las la-map",
    enable: "las la-toggle-on",
    validation: "las la-check-circle",
    createdAt: "las la-calendar-plus",
    updatedAt: "las la-calendar-check",
    id: "las la-id-badge",
    images: "las la-image",
    contact: "las la-envelope",
    phone: "las la-phone",
    link: "las la-link",
    facebook: "lab la-facebook",
    instagram: "lab la-instagram",
    youtube: "lab la-youtube",
  };

  // Tab data
  const tabs = [
    { label: "Informations de base", value: "basic" },
    { label: "Détails", value: "details" },
    { label: "Avancés", value: "advanced" },
  ];

  return (
    <Dialog
      open={open}
      handler={() => console.log}
      className="w-full max-w-2xl mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
    >
      <DialogHeader className="flex items-center justify-center p-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
        <Typography variant="h5" className="font-bold tracking-tight text-center text-white animate-fade-in">
          Détails du Service
        </Typography>
      </DialogHeader>
      <DialogBody className="p-6 max-h-[70vh] overflow-y-auto">
        <Tabs value={activeTab} onChange={(value: any) => setActiveTab(value as string)} className="w-full">
          <TabsHeader className="p-1 bg-gray-100 rounded-lg dark:bg-gray-800">
            {tabs.map(({ label, value }) => (
              <Tab key={value} value={value} className="p-2 text-sm font-semibold">
                {label}
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody className="mt-4">
            {/* Basic Information Tab */}
            <TabPanel value="basic" className="space-y-4">
              {[
                { label: "Titre", value: data?.title, icon: fieldIcons.title },
                { label: "Région", value: data?.region, icon: fieldIcons.region },
                {
                  label: "Prestataire",
                  value: data?.ServiceAgent?.designation || "N/A",
                  icon: fieldIcons.serviceAgent,
                },
                {
                  label: "Disponibilité",
                  value: data?.ServiceAgent?.availability?.join(", ") || "N/A",
                  icon: fieldIcons.availability,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}

              {/* Collapsible Events */}
              <div className="mt-4">
                <Button
                  variant="text"
                  className="flex items-center gap-2 text-blue-600 dark:text-blue-400"
                  onClick={() => toggleCollapse("events")}
                >
                  <i className={fieldIcons.events}></i>
                  Évènements {openCollapse.events ? "▲" : "▼"}
                </Button>
                <Collapse open={openCollapse.events}>
                  <div className="p-4 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800">
                    <Typography className="text-gray-700 dark:text-gray-300">
                      {data?.events?.length ? data.events.join(", ") : "Aucun évènement disponible."}
                    </Typography>
                  </div>
                </Collapse>
              </div>
            </TabPanel>

            {/* Details Tab */}
            <TabPanel value="details" className="space-y-4">
              {[
                {
                  label: "Description",
                  value: formatDescription(data?.description),
                  icon: fieldIcons.description,
                },
                { label: "Contact", value: data?.ServiceAgent?.contact, icon: fieldIcons.contact },
                { label: "Téléphone", value: data?.ServiceAgent?.phone, icon: fieldIcons.phone },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  {item.label === "Description" ? (
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-2">
                        <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                        <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                      </div>
                      <Typography className="font-medium text-gray-900 dark:text-white">
                        {item.value || "N/A"}
                      </Typography>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                        <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                      </div>
                      <Typography className="font-medium text-gray-900 dark:text-white">
                        {item.value || "N/A"}
                      </Typography>
                    </div>
                  )}
                </div>
              ))}

              {/* Social Media Links */}
              <div className="p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md">
                <div className="flex items-center space-x-2">
                  <i className="text-xl text-blue-500 las la-share-alt dark:text-blue-400"></i>
                  <Label className="font-semibold text-gray-700 dark:text-gray-200">Liens :</Label>
                  <div className="flex justify-end flex-1 mt-2 space-x-4 justify-items-end">
                    {data?.ServiceAgent?.link && (
                      <a
                        href={data.ServiceAgent.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 transition-colors duration-200 hover:text-blue-800"
                        title="Site Web"
                      >
                        <i className={`${fieldIcons.link} text-2xl`}></i>
                      </a>
                    )}
                    {data?.ServiceAgent?.facebook && (
                      <a
                        href={data.ServiceAgent.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 transition-colors duration-200 hover:text-blue-800"
                        title="Facebook"
                      >
                        <i className={`${fieldIcons.facebook} text-2xl`}></i>
                      </a>
                    )}
                    {data?.ServiceAgent?.instagram && (
                      <a
                        href={data.ServiceAgent.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 transition-colors duration-200 hover:text-blue-800"
                        title="Instagram"
                      >
                        <i className={`${fieldIcons.instagram} text-2xl`}></i>
                      </a>
                    )}
                    {data?.ServiceAgent?.youtube && (
                      <a
                        href={data.ServiceAgent.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 transition-colors duration-200 hover:text-blue-800"
                        title="YouTube"
                      >
                        <i className={`${fieldIcons.youtube} text-2xl`}></i>
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Images Preview */}
              {data?.images?.length ? (
                <div className="mt-4">
                  <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                    <i className={fieldIcons.images}></i>
                    <Label className="font-semibold">Images</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    {data.images.map((image, index) => (
                      <img
                        key={index}
                        src={`${process.env.REACT_APP_CLOUD_FRONT}${image}`}
                        alt={`Service image ${index + 1}`}
                        className="object-cover w-full h-32 rounded-lg shadow-sm"
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </TabPanel>

            {/* Advanced Details Tab */}
            <TabPanel value="advanced" className="space-y-4">
              {[
                { label: "ID du service", value: data?.id, icon: fieldIcons.id },
                { label: "Activé", value: data?.enable ? "Oui" : "Non", icon: fieldIcons.enable },
                { label: "Validé", value: data?.validation ? "Oui" : "Non", icon: fieldIcons.validation },
                {
                  label: "Créé le",
                  value: data?.createdAt ? new Date(data.createdAt).toLocaleDateString("fr-FR") : "N/A",
                  icon: fieldIcons.createdAt,
                },
                {
                  label: "Mis à jour le",
                  value: data?.updatedAt ? new Date(data.updatedAt).toLocaleDateString("fr-FR") : "N/A",
                  icon: fieldIcons.updatedAt,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
            </TabPanel>
          </TabsBody>
        </Tabs>
      </DialogBody>
      <DialogFooter className="flex justify-center p-4 space-x-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
        <ButtonSecondary
          onClick={handleClose}
          className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Quitter
        </ButtonSecondary>
      </DialogFooter>
    </Dialog>
  );
};

export default ServiceDetailsPage;
